import Link from "next/link";
import { forwardRef } from "react";

import {
  Anchor,
  Badge,
  Button,
  FormativeCheckSVG,
  PowerWordFilledSVG,
  SpanishFilledSVG,
  useModal,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import dayjs from "dayjs";
import { useRouter } from "next/router";

import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";

import { OWN_ORIGIN } from "@/base/base.constants";
import AssignModal from "@/components/AssignModal";
import BasalCard from "@/components/BasalCard";
import { InfoIcons } from "@/components/BasalCard/BasalCard.interface";
import VISIBILITY_MARKER_REASONS from "@/constants/VISIBILITY_MARKER_REASONS";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";

import formatHrefToSupportLegacyLink from "@/utils/formatHrefToSupportLegacyLink";
import {
  ARTICLE_STORY_MAP,
  CONTENT_TYPES_LABELS,
  CONTENT_CARD_READ_UNTIL_DATE_TIME_FORMAT,
  DISABLED_ASSIGNMENT_ACTIONS_TEXT_SET_TYPES,
} from "./ContentCard.constants";
import {
  ContentTypeEnum,
  CardViewmodelAndProps,
} from "./ContentCard.interface";
import {
  addToAssignmentButtonCss,
  cardActionsContainerCss,
  contentCardGradeAndLexileCss,
  contentCardTagsContainerCss,
  contentCardTagCss,
  contentSettingsModeCss,
  linkCss,
  textCss,
} from "./ContentCard.style";
import {
  getAssignHref,
  getReadingSkillsLabels,
  getFormattedGradeLevelLabel,
  getFormattedLexileLevelLabel,
  consolidateReadingSkills,
} from "./ContentCard.utils";

const AnchorWithTrackEvent = withTrackEvent(Anchor);
const ButtonWithTrackEvent = withTrackEvent(Button);

const ContentCard = forwardRef<HTMLElement, CardViewmodelAndProps>(
  (
    {
      id: contentId,
      contentSettingsGroups,
      dataQaSelector,
      description,
      display_date: displayDate,
      images,
      isContentSettingsEnabled,
      metadata,
      title,
      url,
      visibility_markers: visibilityMarkers,
    },
    ref,
  ) => {
    const { data: user } = useUser();
    const { asPath } = useRouter();

    const {
      isLiteUser,
      isParent,
      isStudent,
      isTeacher,
      activeELALicense,
      canSeeFormative,
    } = getUserProperties(user, { shouldUseNewUserApi: false });

    const contentType = metadata.content_type;

    // ARTICLE METADATA
    const articleType =
      "article_type" in metadata ? metadata.article_type : null;
    const dateLiteProductGrantExpires =
      "date_lite_product_grant_expires" in metadata
        ? metadata.date_lite_product_grant_expires
        : null;
    const maturityRange =
      "maturity_range" in metadata ? metadata.maturity_range : null;
    const hasFormative =
      "has_formative" in metadata ? metadata.has_formative : null;
    const hasPowerWords =
      "has_power_words" in metadata ? metadata.has_power_words : null;
    const lexileLevels =
      "lexile_levels" in metadata ? metadata.lexile_levels : null;
    const languages = "languages" in metadata ? metadata.languages : null;
    const readingSkills =
      "reading_skills" in metadata
        ? consolidateReadingSkills(metadata.reading_skills)
        : null;

    // INTERACTIVE VIDEO METADATA
    const durationInSeconds =
      "duration_in_seconds" in metadata ? metadata.duration_in_seconds : null;

    const currentDate = dayjs();
    const dateLiteProductGrantExpiresFormatted =
      !!dateLiteProductGrantExpires && dayjs(dateLiteProductGrantExpires);
    const contentHasExpired =
      currentDate > dateLiteProductGrantExpiresFormatted;
    const showFormativeIcon = hasFormative && canSeeFormative;

    const isFreeContent =
      isLiteUser && dateLiteProductGrantExpires && !contentHasExpired;
    const isFreeArticle = isLiteUser && !!dateLiteProductGrantExpires;
    const showPremiumTag =
      (isLiteUser && (!dateLiteProductGrantExpires || contentHasExpired)) ||
      (isTeacher &&
        (!dateLiteProductGrantExpires || contentHasExpired) &&
        visibilityMarkers?.find(
          (marker) =>
            marker.reason === VISIBILITY_MARKER_REASONS.PREMIUM_CONTENT,
        ));
    const showAdvisoryTag =
      isTeacher &&
      visibilityMarkers?.find(
        (marker) =>
          marker.reason === VISIBILITY_MARKER_REASONS.DISTRICT_ADVISORY ||
          marker.reason === VISIBILITY_MARKER_REASONS.SCHOOL_ADVISORY,
      );
    const showFlaggedTag =
      !isTeacher &&
      visibilityMarkers?.find((marker) => !!marker?.flagged_at_my_admin_level);

    const getLabel = () => {
      if ("label" in metadata) {
        return metadata.label;
      } else if ("article_type" in metadata) {
        return (
          ARTICLE_STORY_MAP[articleType as string] ||
          CONTENT_TYPES_LABELS[contentType]
        );
      } else {
        return CONTENT_TYPES_LABELS[contentType];
      }
    };

    const infoIcons = [
      ...(showFormativeIcon
        ? [
            {
              SvgComponent: FormativeCheckSVG,
              body: "Content has Formative",
              buttonLabel: "Content has Formative",
            },
          ]
        : []),
      ...(hasPowerWords
        ? [
            {
              SvgComponent: PowerWordFilledSVG,
              body: "Content has power words",
              buttonLabel: "Content has power words",
            },
          ]
        : []),
      ...(languages && languages.length > 1
        ? [
            {
              SvgComponent: SpanishFilledSVG,
              body: "Content available in Spanish",
              buttonLabel: "Content available in Spanish",
            },
          ]
        : []),
    ];

    const badges = [
      ...(isFreeContent
        ? [
            {
              text: `Read Until ${dayjs(dateLiteProductGrantExpires).format(CONTENT_CARD_READ_UNTIL_DATE_TIME_FORMAT)}`,
              color: Badge.color.blue,
            },
          ]
        : []),
      ...(showPremiumTag
        ? [
            {
              text: "Premium",
              color: Badge.color.blue,
            },
          ]
        : []),
      ...(showAdvisoryTag
        ? [
            {
              text: "Advisory",
              color: Badge.color.yellow,
            },
          ]
        : []),
      ...(showFlaggedTag
        ? [
            {
              text: "Flagged",
              color: Badge.color.yellow,
            },
          ]
        : []),
    ];

    const canSeeReadingSkills = activeELALicense || isLiteUser;
    const shouldShowReadingSkills =
      canSeeReadingSkills && !!readingSkills?.length;
    const formattedMaturityGradeLevelLabel =
      !!maturityRange && getFormattedGradeLevelLabel(maturityRange);
    const formattedLexileLabel =
      !!lexileLevels && getFormattedLexileLevelLabel(lexileLevels);

    // Assignment actions
    const showAddToAssignmentOption = contentType !== ContentTypeEnum.BUNDLE;
    const showAssignmentActions =
      !isStudent &&
      !isParent &&
      (!isLiteUser || isFreeArticle) &&
      !(
        contentType === ContentTypeEnum.BUNDLE &&
        DISABLED_ASSIGNMENT_ACTIONS_TEXT_SET_TYPES[metadata.label]
      ) &&
      !visibilityMarkers?.find(
        (marker) => marker.reason === VISIBILITY_MARKER_REASONS.PREMIUM_CONTENT,
      );
    const [modalProps, setModalActive] = useModal(false);
    const closeModal = () => {
      setModalActive(false);
    };
    const returnUrl = `${OWN_ORIGIN}${asPath}`;
    const createAssignmentUrl = `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${contentId}`;

    const renderFooterContents = () => {
      return (
        <>
          <div css={contentCardGradeAndLexileCss}>
            {formattedMaturityGradeLevelLabel && (
              <p css={textCss}>
                Maturity: <strong>{formattedMaturityGradeLevelLabel}</strong>
              </p>
            )}
            {formattedLexileLabel && (
              <p css={textCss}>
                Reading: <strong>{formattedLexileLabel}</strong>
              </p>
            )}
          </div>

          {shouldShowReadingSkills && (
            <ul
              aria-label="Article reading skills"
              css={contentCardTagsContainerCss}
            >
              {getReadingSkillsLabels(readingSkills).map((label) => (
                <li key={label} css={contentCardTagCss}>
                  {label}
                </li>
              ))}
            </ul>
          )}

          {isContentSettingsEnabled && (
            <AdminContentSettingsCardFooter
              contentId={contentId}
              contentSettingsGroups={contentSettingsGroups || []}
              visibilityMarkers={visibilityMarkers}
              style={contentSettingsModeCss}
            />
          )}

          {showAssignmentActions && (
            <div css={cardActionsContainerCss}>
              <AnchorWithTrackEvent
                __cssFor={linkCss}
                href={getAssignHref(returnUrl, contentId)}
                actionName={actionNames.navigate}
                objectType={objectTypes.assignment}
                streamProperties={{
                  content_type: contentType,
                  description:
                    "Navigate to create new assignment from content card",
                }}
                __renderCustomLink={({ children, href, ...anchorProps }) => {
                  const linkHref =
                    contentType === ContentTypeEnum.ARTICLE ? url : href;
                  return (
                    <Link
                      href={formatHrefToSupportLegacyLink(
                        typeof linkHref === "string" ? linkHref : "",
                      )}
                      passHref
                      legacyBehavior
                    >
                      <a
                        data-qa-selector="content-card-assign-link"
                        {...anchorProps}
                      >
                        {children}
                      </a>
                    </Link>
                  );
                }}
              >
                Assign
              </AnchorWithTrackEvent>

              {showAddToAssignmentOption && (
                <ButtonWithTrackEvent
                  onClick={() => setModalActive(true)}
                  flavor={Button.flavors.incognito}
                  actionName={actionNames.interact}
                  objectType={objectTypes.content}
                  streamProperties={{
                    content_type: contentType,
                    description: "Open add to assignment modal on content card",
                  }}
                  dataProps={{
                    "data-qa-selector":
                      "content-card-open-add-to-assignment-modal",
                  }}
                  __cssFor={addToAssignmentButtonCss}
                >
                  Add to assignment
                </ButtonWithTrackEvent>
              )}
            </div>
          )}
          {modalProps.isActive && (
            <AssignModal
              contentMemberIds={[contentId]}
              createAssignmentUrl={createAssignmentUrl}
              modalProps={modalProps}
              handleClickToCloseModal={closeModal}
            />
          )}
        </>
      );
    };

    return (
      <BasalCard
        ref={ref}
        badges={badges}
        contentId={contentId}
        dataQaSelector={dataQaSelector}
        displayDate={displayDate}
        duration={durationInSeconds}
        footer={renderFooterContents()}
        href={url}
        images={images}
        infoIcons={infoIcons as InfoIcons[]}
        title={title}
        label={getLabel()}
        subHeader={description}
      />
    );
  },
);

ContentCard.displayName = "ContentCard";
export default ContentCard;
